import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import {
  HsCameraToolType,
  HsCameraToolTypeControlAction,
  HsDefaultMaterial,
  HsGraphicCollection,
  HsMatrix4,
  HsQuaternion,
  HsShaderData,
  HsShaderObjectType,
  HsVector3,
  hsGenerateCubeGeometryData,
  hsIndexGeometry,
  hsMakeIndexedGeometry,
  type HsGraphics,
  type HsHypersceneViewer,
  type HsIndexedGeometry,
  type HsMaterial,
  type HsViewerInputOptions,
} from '@sibe/hyperscene';
import { DemoViewerPage } from '../../classes/demo-viewer-page.class';

const GRID_SIZE: number = 80;
const GRID_STEP: number = 3;

// Collection instances keep absolute matrices, so the camera orbits the grid by the inverse rotation instead
// of rewriting 512,000 instance matrices every frame; the relative motion is the same.
const CAMERA_ROTATION: HsMatrix4 = HsMatrix4.compose(
  [0, 0, 0],
  HsQuaternion.makeFromAxisAndAngle(new HsVector3(1, 0.5, 0.25).normalize().values, -0.01).values,
  [1, 1, 1]
);

/** 80 x 80 x 80 = 512,000 cubes drawn as one instanced collection, rotating as a whole. */
@Component({
  selector: 'hyperscene-500000-items',
  templateUrl: './demo-with-500000-items.component.html',
  styleUrls: ['./demo-with-500000-items.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DemoWith500000ItemsComponent extends DemoViewerPage {
  private collection: HsGraphicCollection | null = null;

  protected override inputOptions(): HsViewerInputOptions | null {
    return null;
  }

  protected initScene(viewer: HsHypersceneViewer): void {
    const graphics: HsGraphics = viewer.surface.graphics;
    const geometry: HsIndexedGeometry | null = hsMakeIndexedGeometry(
      graphics,
      hsIndexGeometry(hsGenerateCubeGeometryData())
    );
    if (geometry === null) {
      return;
    }
    const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.collection);
    shaderData.addColor().addDepth().addNormals().addDiffuseLighting();
    const material: HsMaterial = new HsDefaultMaterial(viewer.gl, shaderData.makeShader());
    const color: readonly number[] = [1, 0.5, 0.25];

    const collection: HsGraphicCollection = new HsGraphicCollection(getUuid(), 'cubes', geometry, material);
    const matrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
    const gridCenter: number = (GRID_SIZE * GRID_STEP) / 2;
    for (let i: number = 0; i < GRID_SIZE; i++) {
      for (let j: number = 0; j < GRID_SIZE; j++) {
        for (let k: number = 0; k < GRID_SIZE; k++) {
          matrix.setPosition([i * GRID_STEP - gridCenter, j * GRID_STEP - gridCenter, k * GRID_STEP - gridCenter]);
          collection.addInstance(matrix.values, color);
        }
      }
    }
    collection.updateCenter();
    collection.updateBoundingSphere();
    collection.updateBuffers(graphics);
    this.collection = collection;
    viewer.add(collection);

    // The page drives the camera itself; the camera tools would fight the orbit.
    viewer.controls.updateControlAction(new HsCameraToolTypeControlAction(HsCameraToolType.None));
    viewer.camera.worldMatrix.setPosition([0, 0, 500]);
  }

  protected override animate(viewer: HsHypersceneViewer): boolean {
    if (this.collection === null) {
      return false;
    }
    viewer.camera.worldMatrix.premultiply(CAMERA_ROTATION);
    viewer.camera.updateCameraMatrixInverse();
    viewer.camera.updateProjectionMatrix();
    return true;
  }
}
